import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useFulfillmentSummary } from '@/clients/order/useFulfillmentSummary';
import {
    BILLING_TYPE,
    ORDER_ORGANIZATION_TRANSACTIONAL_ROLE,
    PaymentOrganization,
    SummaryOrganization,
} from '@schuettflix/interfaces';
import type { BillingAddress } from '@/clients/organization/types/organization.ts';

/**
 * A partner invoice may leave the payment term unset, every other billing method requires one.
 */
type FinanceSettingsUpdate =
    | {
          billingMethod: Extract<BILLING_TYPE, 'PARTNER_INVOICE'>;
          paymentTermId: number | null;
          transactionalRole: ORDER_ORGANIZATION_TRANSACTIONAL_ROLE;
      }
    | {
          billingMethod: Exclude<BILLING_TYPE, 'PARTNER_INVOICE'>;
          paymentTermId: number;
          transactionalRole: ORDER_ORGANIZATION_TRANSACTIONAL_ROLE;
      };

export interface UpdateOrganizationFinanceSettingsPayload {
    financeSettings: FinanceSettingsUpdate;
    organizationId: number;
}

type OrganizationsFinanceSettings = (SummaryOrganization & {
    orgName: string;
    orgBillingAddress: BillingAddress;
})[];
type TransactionalRole = UpdateOrganizationFinanceSettingsPayload['financeSettings']['transactionalRole'];
type LocalFinanceSettings = Omit<UpdateOrganizationFinanceSettingsPayload['financeSettings'], 'transactionalRole'>;

type FinanceSettingsLocalStateContext = {
    organizationsFinanceSettings: OrganizationsFinanceSettings;
    updateOrganizationFinanceSettings: (payload: UpdateOrganizationFinanceSettingsPayload) => void;
    getPaymentOrganizationsByProductId: (productId: string) => PaymentOrganization[];
};

const FinanceSettingsLocalStateContext = createContext<FinanceSettingsLocalStateContext | null>(null);

const toRecordKey = ({
    organizationId,
    transactionalRole,
}: {
    organizationId: number;
    transactionalRole: TransactionalRole;
}) => `${organizationId}_${transactionalRole}`;

export const FinanceSettingsLocalStateProvider = ({
    children,
}: {
    children?: ReactNode | ((financeSettingsLocalStateContext: FinanceSettingsLocalStateContext) => ReactNode);
}) => {
    const {
        data: { organizations: organizationsFinanceSettingsFromApi },
    } = useFulfillmentSummary();
    const [organizationFinanceSettingsOverrideRecord, setOrganizationFinanceSettingsOverrideRecord] = useState<
        Record<string, LocalFinanceSettings | undefined>
    >({});

    const updateOrganizationFinanceSettings = ({
        organizationId,
        financeSettings: { transactionalRole, ...financeSettings },
    }: UpdateOrganizationFinanceSettingsPayload) => {
        setOrganizationFinanceSettingsOverrideRecord(previousSettings => ({
            ...previousSettings,
            [toRecordKey({ organizationId, transactionalRole })]: financeSettings,
        }));
    };

    const organizationsFinanceSettings = useMemo(
        () =>
            organizationsFinanceSettingsFromApi.map(orgSettings => ({
                ...orgSettings,
                financeSettings: orgSettings.financeSettings.map(financeSettings => {
                    const override =
                        organizationFinanceSettingsOverrideRecord[
                            toRecordKey({
                                organizationId: orgSettings.orgId,
                                transactionalRole: financeSettings.transactionalRole,
                            })
                        ];
                    if (!override) return financeSettings;
                    return {
                        ...financeSettings,
                        selected: {
                            ...financeSettings.selected,
                            ...override,
                        },
                    };
                }),
            })),
        [organizationFinanceSettingsOverrideRecord, organizationsFinanceSettingsFromApi]
    );

    const getPaymentOrganizationsByProductId = useCallback<
        FinanceSettingsLocalStateContext['getPaymentOrganizationsByProductId']
    >(
        productId => {
            return organizationsFinanceSettings.flatMap(orgSettings =>
                orgSettings.transactions.flatMap(({ transactionalRole, ...transaction }) => {
                    if (transaction.productId !== productId) return [];
                    const matchedFinanceSettings = orgSettings.financeSettings.find(
                        financeSettings => financeSettings.transactionalRole === transactionalRole
                    );
                    if (!matchedFinanceSettings) return [];
                    return transaction.functionalRoles.map(functionalRole => ({
                        ...matchedFinanceSettings.selected,
                        billingText: matchedFinanceSettings.billingText,
                        transactionalRole,
                        organizationId: orgSettings.orgId,
                        functionalRole,
                    }));
                })
            );
        },
        [organizationsFinanceSettings]
    );

    const contextValue = {
        organizationsFinanceSettings,
        updateOrganizationFinanceSettings,
        getPaymentOrganizationsByProductId,
    };
    return (
        <FinanceSettingsLocalStateContext.Provider value={contextValue}>
            {typeof children === 'function' ? children(contextValue) : children}
        </FinanceSettingsLocalStateContext.Provider>
    );
};

export const useMaybeFinanceSettingsLocalState = () => useContext(FinanceSettingsLocalStateContext);

export const useFinanceSettingsLocalState = () => {
    const context = useContext(FinanceSettingsLocalStateContext);
    if (!context) {
        throw new Error('useFinanceSettingsLocalState must be used within a FinanceSettingsLocalStateProvider');
    }
    return context;
};
